import { noop } from 'lodash';
import { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { ObjectiveItem, ObjectiveItemUpdate } from '~src/store/okrs/interfaces';
import { deleteObjective, updateObjective } from '~src/store/okrs/actions';
import { PrimarySection, SecondarySection } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import { StoreI } from '~src/rootReducer';
import { SessionUserI } from '~src/interfaces/session.types';
import SearchableActivityList from '~src/modules/okrs/shared/activities/SearchableActivityList';
import { showNotification } from '~src/store/app/actions';
import { ShowNotification } from '~src/store/app/interfaces';
import NavArrowLnk from '~src/shared/buttons/NavArrowLnk';
import SvgIcon from '~src/shared/SvgIcon';
import { routes } from '~src/routingConfig';
import { DdMenu } from '~src/shared/menus/dropdownBtn/DropdownBtn';
import { MenuItemDel } from '~src/shared/headers/itemDetalsHeader/itemDetailsHeader.sc';
import IconTextItem from '~src/shared/IconTextItem';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import { txtOrKey } from '~src/utils';
import { ApiReduxResp, ThunkAnyAction } from '~src/services/apiMiddleware';
import { ObjectiveI } from '~src/store/okrs/keyResultStream/interfaces';
import { showApiError } from '~src/shared/api/notification';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import PeopleChips from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { ownerToSegment } from '~src/modules/okrs/manageObjectives/shared/utils';

import InfoEdit from './InfoEdit';
import { MarginContainer, ObjectiveHeader, PageEditContainer } from './objectiveDetailsForm.sc';
import OwnersEdit from './OwnersEdit';
import SubInfoEdit from './SubInfoEdit';
import StatusSection from './StatusEdit';
import LabelsEdit from './LabelsEdit';
import EditKeyResultList from './keyResult/EditKeyResultList';
import { IconWrapper } from './keyResult/keyResultDetails.sc';
import EditSubObjectiveList from './subObjective/EditSubObjectiveList';

interface OwnProps {
  objective: ObjectiveItem;
  reloadObjective: () => void;
  key: string; // uncontrolled components inside, do use objective.id as key!
}

interface DispatchProps {
  editObjective: (objectiveId: string, objective: ObjectiveItemUpdate) => any;
  showNotification: ShowNotification;
  deleteObjective: (ObjectiveId: string) => ApiReduxResp;
}

interface StateProps {
  sessionUser: SessionUserI | null;
}

type Props = OwnProps & DispatchProps & StateProps & RouteComponentProps<any>;

const ObjectiveDetailsForm: FC<Props> = props => {
  const { objective, sessionUser } = props;
  const [showDelConfirm, setShowDelConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reloadActivities, setReloadActivities] = useState(false);
  const [t] = useTranslation();

  const onDelClick = () => {
    setShowDelConfirm(true);
  };

  const hideDelModal = () => {
    setShowDelConfirm(false);
  };

  const goBack = () => {
    history.back();
  };

  const deleteObjectiveFn = async () => {
    setDeleteLoading(true);
    try {
      if (objective !== null) {
        const rsp = await props.deleteObjective(objective.id);
        if (rsp.error === undefined) {
          props.showNotification({ id: 'delete_success', txtKey: 'x.okr.objective.deleted' });
          props.history.push(routes.okrs.list);
        } else {
          props.showNotification({
            id: 'delete_error',
            txtKey: 'x.okr.error.weighting_for_objective_exceeded',
          });
        }
      }
      setDeleteLoading(false);
      hideDelModal();
    } catch (err) {
      props.showNotification({ txtKey: String(err), id: 'delete_objective_error' });
    }
  };
  const menu = (
    <DdMenu>
      <MenuItemDel onClick={onDelClick}>
        <IconTextItem iconName="iconBin" labelKey={'x.objective.delete'} label={t('x.objective.delete')} />
      </MenuItemDel>
    </DdMenu>
  );

  const updateObjectiveMerge = (values: Partial<ObjectiveItemUpdate>) => {
    return props
      .editObjective(objective.id, {
        ...objective,
        ...values,
        owners: values.owners || (objective.owners ? objective.owners.map(owner => owner.uuid) : []),
        visibilities:
          values.visibilities ||
          (objective.visibilities ? objective.visibilities.map(person => person.id) : undefined),
        labels: values.labels || (objective.labels ? objective.labels.map(label => label.id) : undefined),
      })
      .then((rsp: ThunkAnyAction<ObjectiveI>) => {
        if (rsp.error) {
          rsp.error.response.data.forEach(showApiError);
          return;
        }
        props.showNotification({ id: 'objective_save', txtKey: 'x.objective.details.change.saved' });
      })
      .finally(() => {
        setReloadActivities(!reloadActivities);
      });
  };

  const hasChangePermissions = useMemo<boolean>(
    () =>
      sessionUser !== null &&
      (sessionUser.uuid === objective.creator_id ||
        (objective.owners || []).findIndex(owner => owner.uuid === sessionUser.uuid) !== -1),
    [sessionUser, objective],
  );

  const basicProps = {
    hasChangePermissions,
    update: updateObjectiveMerge,
    objective,
  };

  return (
    <>
      <ActionConfirmDialog
        t={t}
        show={showDelConfirm}
        close
        onHide={hideDelModal}
        actionTitleKey={'x.objective.delete.modal.title'}
        actionContentKey={null}
        actionCall={deleteObjectiveFn}
        showDeletion
        loadingAction={deleteLoading}
      />
      <ObjectiveHeader>
        <div onClick={goBack} style={{ textDecoration: 'none' }}>
          <NavArrowLnk>
            <span style={{ whiteSpace: 'nowrap' }} data-translation={'backArrow.key'}>
              {txtOrKey({ key: 'x.survey.detail.header.back' }, t)}
            </span>
          </NavArrowLnk>
        </div>
        {hasChangePermissions && (
          <Dropdown
            overlay={menu}
            trigger={['click']}
            overlayClassName="lls-dropdown-menu"
            placement="bottomRight"
          >
            <IconWrapper>
              <SvgIcon name="iconThreeDots" />
            </IconWrapper>
          </Dropdown>
        )}
      </ObjectiveHeader>
      <PageEditContainer>
        <PrimarySection>
          <InfoEdit {...basicProps} />
          <EditKeyResultList
            frozen={!hasChangePermissions}
            objective={objective}
            onUpdateKeyResult={props.reloadObjective}
          />
          <EditSubObjectiveList readonly={!hasChangePermissions} superObjective={props.objective} />
          <SearchableActivityList type="objective" id={objective.id} />
        </PrimarySection>
        <SecondarySection>
          <MarginContainer>
            <StatusSection {...basicProps} />
            <SubInfoEdit {...basicProps} />
            <OwnersEdit {...basicProps} />
            <SelectorHandler hoverTextKey="x.objective.add.icon.creator" iconName="iconPersonOutline">
              {() => (
                //doesn't feel good but both above and bottom elements are common elements
                <div style={{ marginTop: '5px' }}>
                  <PeopleChips
                    locked
                    chipsName="creator"
                    selectedPeople={[ownerToSegment(objective.creator)]}
                    t={t}
                    placeHolderKey="x.okrs.objective.visibility.chips.placeholder"
                    onChangePeople={noop}
                  />
                </div>
              )}
            </SelectorHandler>
            <LabelsEdit {...basicProps} />
          </MarginContainer>
        </SecondarySection>
      </PageEditContainer>
    </>
  );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  editObjective: (objective, id) => dispatch(updateObjective(objective, id)),
  showNotification: notification => dispatch(showNotification(notification)),
  deleteObjective: objectiveId => dispatch(deleteObjective(objectiveId)),
});

const mapStateToProps = (state: StoreI): StateProps => ({
  sessionUser: state.user.session.user,
});

const ObjectiveDetailsFormConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ObjectiveDetailsForm));

export default ObjectiveDetailsFormConnected;
